/**
 * ExpensesLayout
 * Wrapper with horizontal tab navigation for the Expenses section.
 * Implements a scrollable tab bar with dynamic edge fades that signal overflow.
 * The active tab is scrolled into view on every route change.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useRef, useState, useEffect, useCallback } from 'react';

interface Tab {
  label: string;
  path: string;
  end?: boolean;
  testId: string;
}

const TABS: Tab[] = [
  { label: 'Show Expenses',                path: '/expenses',              end: true,  testId: 'expenses-tab-show' },
  { label: 'Confirm Expenses',             path: '/expenses/confirm',      end: false, testId: 'expenses-tab-confirm' },
  { label: 'Manage Recurring Expenses',   path: '/expenses/recurring',    end: false, testId: 'expenses-tab-recurring' },
  { label: 'Manage Expense Aggregations', path: '/expenses/aggregations', end: false, testId: 'expenses-tab-aggregations' },
];

interface ExpensesLayoutProps {
  children?: ReactNode;
}

export function ExpensesLayout({ children }: ExpensesLayoutProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const location = useLocation();

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 1);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    el.addEventListener('scroll', updateFades, { passive: true });
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateFades);
      ro.disconnect();
    };
  }, [updateFades]);

  // Scroll the active tab into view on every route change
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeLink = el.querySelector('[aria-current="page"]') as HTMLElement | null;
    activeLink?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="expenses-layout">
      {/* Tab navigation */}
      <nav
        data-testid="expenses-nav"
        aria-label="Expenses navigation"
        className="px-[var(--page-padding-x)] mb-6"
      >
        {/* Border lives on this container so it spans full width regardless of scroll */}
        <div className="relative border-b border-border">
          {/* Left fade — signals content scrolled off to the left */}
          {showLeftFade && (
            <div
              className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-background to-transparent"
              aria-hidden="true"
            />
          )}
          {/* Right fade — signals content scrolled off to the right */}
          {showRightFade && (
            <div
              className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-background to-transparent"
              aria-hidden="true"
            />
          )}
          <div
            ref={scrollRef}
            className="flex gap-1 overflow-x-auto"
            style={{ scrollbarWidth: 'none' }}
          >
            {TABS.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.end}
                data-testid={tab.testId}
                className={({ isActive }) =>
                  [
                    'px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                  ].join(' ')
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Page content */}
      {children ?? <Outlet />}
    </div>
  );
}

export default ExpensesLayout;
